import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Building2,
    CalendarDays,
    Mail,
    ShieldCheck,
    X,
} from 'lucide-react';
import stripeApi from '../../../api/stripeApi';
import userMetadataApi from '../../../api/userMetadataApi';
import { StripeLinkedAccount, StripeLinkedAccountsResponse, UserMetadataResponse } from '../../../api/types';

interface CurrentBalanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    businessId?: string;
    currentBalanceTotal?: number | null;
}

const formatMoney = (value: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
    }).format(value);

const formatDateTime = (value?: string | null) => {
    if (!value) return 'Not available';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Not available';

    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const getAccountBalance = (account: StripeLinkedAccount) => {
    const latest = account.latest_balance || {};
    const value =
        account.current_balance ??
        account.balance_state?.derived_current ??
        account.balance_state?.baseline_current ??
        latest.current_usd ??
        latest.available_balance ??
        latest.current_balance ??
        0;
    return Number.isFinite(Number(value)) ? Number(value) : 0;
};

const getCreditUsedBalance = (account: StripeLinkedAccount) => {
    const value =
        account.current_used_balance ??
        account.balance_state?.derived_used ??
        account.balance_state?.baseline_used;
    return Number.isFinite(Number(value)) ? Number(value) : null;
};

const getAccountCurrency = (account: StripeLinkedAccount) =>
    account.latest_balance?.currency || 'USD';

const getAccountSyncedAt = (account: StripeLinkedAccount) =>
    account.stripe_synced_at ||
    account.stripe_transaction_synced_at ||
    account.stripe_balance_synced_at ||
    account.latest_balance?.fetched_at ||
    account.last_refreshed_at ||
    account.linked_at ||
    null;

const getAccountTypeLabel = (account: StripeLinkedAccount) => {
    const typeHints = [
        account.balance_type,
        account.category,
        account.account_type,
        account.subcategory,
        account.display_name,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    return /(credit|card|loan|line)/.test(typeHints) ? 'Credit' : 'Cash';
};

const getAccountStatusTone = (status?: string | null) => {
    const normalized = String(status || 'active').toLowerCase();
    if (normalized === 'active') return 'bg-emerald-50 text-emerald-700';
    if (normalized === 'inactive') return 'bg-amber-50 text-amber-700';
    if (normalized === 'disconnected') return 'bg-rose-50 text-rose-700';
    return 'bg-slate-100 text-slate-700';
};

const CurrentBalanceModal: React.FC<CurrentBalanceModalProps> = ({
    isOpen,
    onClose,
    title,
    businessId,
    currentBalanceTotal,
}) => {
    const { data: linkedAccountsData, isLoading: accountsLoading } = useQuery<StripeLinkedAccountsResponse>({
        queryKey: ['dashboard-current-balance-accounts', businessId],
        queryFn: () => stripeApi.getLinkedAccounts(businessId),
        enabled: isOpen,
        retry: false,
    });

    const { data: userMetadata, isLoading: metadataLoading } = useQuery<UserMetadataResponse>({
        queryKey: ['dashboard-current-balance-user-metadata', businessId],
        queryFn: () => userMetadataApi.getUserMetadata(businessId),
        enabled: isOpen,
    });

    const activeAccounts = useMemo(
        () => (linkedAccountsData?.linked_accounts || []).filter((account) => account.status === 'active'),
        [linkedAccountsData]
    );

    const cashAccounts = useMemo(
        () => activeAccounts.filter((account) => getAccountTypeLabel(account) === 'Cash'),
        [activeAccounts]
    );

    const creditAccounts = useMemo(
        () => activeAccounts.filter((account) => getAccountTypeLabel(account) === 'Credit'),
        [activeAccounts]
    );

    const displayedCashTotal = useMemo(
        () => cashAccounts.reduce((sum, account) => sum + getAccountBalance(account), 0),
        [cashAccounts]
    );

    const headerCashTotal = typeof currentBalanceTotal === 'number' && Number.isFinite(currentBalanceTotal)
        ? currentBalanceTotal
        : displayedCashTotal;

    const accountSummary = useMemo(() => {
        const latestSync = [...activeAccounts]
            .map((account) => getAccountSyncedAt(account))
            .filter(Boolean)
            .sort()
            .at(-1);

        return {
            latestSync: latestSync || userMetadata?.connections?.qbo_last_sync || null,
        };
    }, [activeAccounts, userMetadata]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex flex-col sm:items-center sm:justify-center overflow-hidden">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" onClick={onClose} />

            <div
                className="relative bg-white shadow-xl transform transition-all 
                /* Mobile: Full Screen */
                w-full h-full flex flex-col py-8
                /* Desktop: Centered Box */
                sm:rounded-lg sm:max-w-6xl sm:h-[60vh] sm:my-8 sm:py-0"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                    <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-bold leading-6 text-gray-900 truncate">
                            {title}
                        </h3>
                        <p className="mt-0.5 text-xs sm:text-sm text-gray-500">
                            Cash balance total: <span className="font-semibold text-gray-900">{formatMoney(headerCashTotal)}</span>
                            <span className="mx-2 text-gray-300">|</span>
                            <span>{activeAccounts.length} active accounts shown</span>
                            <span className="mx-2 text-gray-300">|</span>
                            <span>{cashAccounts.length} cash accounts included in total</span>
                            <span className="mx-2 text-gray-300">|</span>
                            <span>Latest sync {formatDateTime(accountSummary.latestSync)}</span>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="ml-4 bg-gray-50 p-2 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none transition-colors"
                    >
                        <X size={20} className="sm:w-6 sm:h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto bg-slate-50">
                    <div className="h-full bg-white">
                        {(accountsLoading || metadataLoading) ? (
                            <div className="px-6 py-8 text-sm text-gray-500">Loading account details...</div>
                        ) : activeAccounts.length === 0 ? (
                            <div className="px-6 py-8 text-sm text-gray-500">
                                No active bank accounts are connected for this business yet.
                            </div>
                        ) : (
                            <div className="min-w-full overflow-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Account</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Institution</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Type</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Balance</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Available</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Synced</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                                        </tr>
                                        {cashAccounts.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-4 py-6 text-sm text-gray-500">
                                                    No active cash accounts.
                                                </td>
                                            </tr>
                                        ) : (
                                            cashAccounts.map((account) => {
                                                const balance = getAccountBalance(account);
                                                const syncedAt = getAccountSyncedAt(account);
                                                const currency = getAccountCurrency(account);
                                                const typeLabel = getAccountTypeLabel(account);
                                                const availableBalance =
                                                    account.current_available_balance ??
                                                    account.balance_state?.derived_available ??
                                                    account.balance_state?.baseline_available ??
                                                    account.latest_balance?.available_balance;
                                                const displayName = account.display_name || 'Connected account';
                                                const accountId = account._id || account.financial_account_id;

                                                return (
                                                    <tr key={accountId} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-4 align-top">
                                                            <div className="text-sm font-semibold text-gray-900">{displayName}</div>
                                                            <div className="mt-1 text-xs text-gray-500">
                                                                {account.last4 ? `•••• ${account.last4}` : account.financial_account_id}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                                                            {account.institution_name || 'Not available'}
                                                        </td>
                                                        <td className="px-4 py-4 align-top">
                                                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                                                                {typeLabel}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-4 align-top text-right text-sm font-semibold text-gray-900">
                                                            {formatMoney(balance, currency)}
                                                        </td>
                                                        <td className="px-4 py-4 align-top text-right text-sm text-gray-700">
                                                            {typeof availableBalance === 'number'
                                                                ? formatMoney(availableBalance, currency)
                                                                : 'Not available'}
                                                        </td>
                                                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                                                            {formatDateTime(syncedAt)}
                                                        </td>
                                                        <td className="px-4 py-4 align-top">
                                                            <span className={`rounded-full px-2 py-1 text-xs font-medium capitalize w-fit ${getAccountStatusTone(account.status)}`}>
                                                                {account.status || 'active'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}

                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Account</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Institution</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Type</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Balance</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Used</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Synced</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                                        </tr>
                                        {creditAccounts.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-4 py-6 text-sm text-gray-500">
                                                    No active credit accounts.
                                                </td>
                                            </tr>
                                        ) : (
                                            creditAccounts.map((account) => {
                                                const balance = getAccountBalance(account);
                                                const usedBalance = getCreditUsedBalance(account);
                                                const syncedAt = getAccountSyncedAt(account);
                                                const currency = getAccountCurrency(account);
                                                const displayName = account.display_name || 'Connected account';
                                                const accountId = account._id || account.financial_account_id;
                                                const typeLabel = getAccountTypeLabel(account);

                                                return (
                                                    <tr key={accountId} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-4 align-top">
                                                            <div className="text-sm font-semibold text-gray-900">{displayName}</div>
                                                            <div className="mt-1 text-xs text-gray-500">
                                                                {account.last4 ? `•••• ${account.last4}` : account.financial_account_id}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                                                            {account.institution_name || 'Not available'}
                                                        </td>
                                                        <td className="px-4 py-4 align-top">
                                                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                                                                {typeLabel}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-4 align-top text-right text-sm font-semibold text-gray-900">
                                                            {formatMoney(balance, currency)}
                                                        </td>
                                                        <td className="px-4 py-4 align-top text-right text-sm text-gray-700">
                                                            {typeof usedBalance === 'number'
                                                                ? formatMoney(usedBalance, currency)
                                                                : 'Not available'}
                                                        </td>
                                                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                                                            {formatDateTime(syncedAt)}
                                                        </td>
                                                        <td className="px-4 py-4 align-top">
                                                            <span className={`rounded-full px-2 py-1 text-xs font-medium capitalize w-fit ${getAccountStatusTone(account.status)}`}>
                                                                {account.status || 'active'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* {userMetadata?.connections?.linked_qbo && (
                            <div className="border-t border-gray-200 bg-slate-50 px-4 py-3">
                                <div className="grid grid-cols-1 gap-3 text-sm text-gray-600 sm:grid-cols-4">
                                    <div>
                                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-400">
                                            <Building2 size={14} />
                                            Company
                                        </div>
                                        <div className="mt-1 font-medium text-gray-900">
                                            {userMetadata.qbo_metadata?.company_name || 'QuickBooks connected'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-400">
                                            <Mail size={14} />
                                            Email
                                        </div>
                                        <div className="mt-1 font-medium text-gray-900">
                                            {userMetadata.qbo_metadata?.company_email || 'Not available'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-400">
                                            <CalendarDays size={14} />
                                            Fiscal year
                                        </div>
                                        <div className="mt-1 font-medium text-gray-900">
                                            {userMetadata.qbo_metadata?.fiscal_year_period || 'Not available'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-400">
                                            <ShieldCheck size={14} />
                                            QBO sync
                                        </div>
                                        <div className="mt-1 font-medium text-gray-900">
                                            {formatDateTime(userMetadata.connections?.qbo_last_sync)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )} */}
                    </div>
                </div>

                <div className="h-4 sm:hidden bg-white" />
            </div>
        </div>
    );
};

export default CurrentBalanceModal;
